import type { TransactionChangePayment, TransactionChangeRow } from "../../api/specialized.ts";

/* The refund rules of AdminServerService/Domain/RefundAllocation.vb, for the Edit Transaction
 * confirm step's preview. The server is the authority and re-checks everything; keep the source,
 * order and allocation rules here in step with it. One deliberate difference: the preview
 * allocates price + tax combined, while the server splits them separately as the provider
 * receives them, and says so in its partial-success message when that changes the outcome. */

export interface RefundSource {
    id: number;
    type: string;
    charged: number;
    refunded: number;
    remaining: number;
    previouslyRefunded: boolean;
    /** ResultData matches Admin_RefundFilter — the server refuses to refund it. */
    blocked: boolean;
}

export interface RefundAllocationStep {
    id: number;
    amount: number;
}

const EPS = 0.005;
const round2 = (n: number) => Math.round(n * 100) / 100;
const typeIs = (p: TransactionChangePayment, t: string) => (p.Type ?? "").toUpperCase() === t;

function filterPattern(filter: string | undefined): RegExp | null {
    if (!filter) return null;
    try { return new RegExp(filter, "i"); } catch { return null; }
}

/** Card payments (CAPTURE / DIRECTCAPTURE, not a PayaP credit) with money left after the refund
 *  rows that name them. SUBSIDY and EXTERNAL are never sources. */
export function refundSources(payments: TransactionChangePayment[], refundFilter?: string): RefundSource[] {
    const pattern = filterPattern(refundFilter);
    const out: RefundSource[] = [];
    for (const p of payments) {
        if (!typeIs(p, "CAPTURE") && !typeIs(p, "DIRECTCAPTURE")) continue;
        if ((p.CustomData ?? "").toUpperCase() === "PAYAPCREDIT") continue;
        const charged = round2((p.Price ?? 0) + (p.Tax ?? 0));
        // Refund rows arrive negated from S_UpdateTransaction_GetInfo_SP.
        const refunded = round2(payments
            .filter(r => typeIs(r, "REFUND") && r.RefundStatus === p.ID)
            .reduce((s, r) => s - ((r.Price ?? 0) + (r.Tax ?? 0)), 0));
        const remaining = round2(charged - refunded);
        if (remaining <= EPS) continue;
        out.push({
            id: p.ID,
            type: p.Type,
            charged,
            refunded,
            remaining,
            previouslyRefunded: refunded > EPS,
            blocked: pattern !== null && pattern.test(p.ResultData ?? ""),
        });
    }
    return out;
}

/** The server's order when none is named: never refunded first, then those covering the whole
 *  refund, then the smallest, then by id. */
export function defaultRefundOrder(sources: RefundSource[], amount: number): RefundSource[] {
    const short = (s: RefundSource) => Number(s.remaining < amount - EPS);
    return [...sources].sort((a, b) =>
        Number(a.previouslyRefunded) - Number(b.previouslyRefunded)
        || short(a) - short(b)
        || a.remaining - b.remaining
        || a.id - b.id);
}

/** Take from each source in order until `amount` is covered. */
export function allocateRefund(ordered: RefundSource[], amount: number): { steps: RefundAllocationStep[]; uncovered: number } {
    let need = round2(Math.max(0, amount));
    const steps: RefundAllocationStep[] = [];
    for (const s of ordered) {
        if (need <= EPS) break;
        const take = round2(Math.min(s.remaining, need));
        if (take <= EPS) continue;
        steps.push({ id: s.id, amount: take });
        need = round2(need - take);
    }
    return { steps, uncovered: need > EPS ? need : 0 };
}

export interface SubsidyAdjustment {
    /** Package-deal id — the subsidy package row's Product, the SUBSIDY payment's SubsidyID. */
    subsidyId: number;
    /** The SUBSIDY payment row the save will insert: negative reverses subsidy already paid. */
    delta: number;
}

/** The SUBSIDY rows the save will insert, one per subsidy that is out of step: its package rows
 *  (36, negative, price × qty) against the SUBSIDY payments naming it. Per subsidy, as
 *  S_UpdateTransaction_UpdateFull_SP syncs them — each is its own sponsor, so one paid too much and
 *  another too little are two rows, not zero. Empty without a row 36, because the procedure syncs
 *  only when one is posted. */
export function subsidyAdjustments(rows: TransactionChangeRow[], payments: TransactionChangePayment[]): SubsidyAdjustment[] {
    const owed = new Map<number, number>();
    for (const r of rows) {
        if (r.RowType !== 36) continue;
        owed.set(r.Product, (owed.get(r.Product) ?? 0) - ((r.InitialAmount ?? 0) + (r.InitialTax ?? 0)) * (r.Quantity ?? 1));
    }
    const out: SubsidyAdjustment[] = [];
    for (const [subsidyId, owedTotal] of owed) {
        const paid = payments
            .filter(p => typeIs(p, "SUBSIDY") && p.SubsidyID === subsidyId)
            .reduce((s, p) => s + (p.Price ?? 0) + (p.Tax ?? 0), 0);
        const delta = round2(owedTotal - paid);
        if (Math.abs(delta) >= EPS) out.push({ subsidyId, delta });
    }
    return out;
}
